import { createResource, createSignal, For, Show } from 'solid-js';
import { A } from '@solidjs/router';
import Modal from '../Modal';
import Text from '../Text';
import PlayerHeadAvatar from '../PlayerHeadAvatar';
import { Badge } from '../Badge';
import { InlineEdit } from '../InlineEdit';
import { showConfirm } from '../Dialog';
import { useToast } from '../MessageBox';
import { useApi, isOk } from '../../api';
import { firstError } from '../../api/envelope';
import { useTranslation } from '../../hooks/useTranslation';
import { usePaymentChanges } from '../../hooks/usePaymentChanges';
import { formatDateTime } from '../../services/dateFormat';
import { formatCurrencyExact } from '../../services/numberFormat';
import { stripColorCodes } from '../../services/itemDisplayName';
import { PAYMENT_STATUS_TONE, balanceChangeLabelKey, entryIcon, isBalanceChange, referenceHref, signedAmount } from '../../services/payments';
import type { PaymentLedgerEntryDetailDto } from '../../api';
import styles from './Payments.module.css';

interface Props {
    botId: string;
    entryId: string | null;
    onClose: () => void;
    onChanged: () => void;
}

export default function PaymentEntryModal(props: Props) {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const [resolving, setResolving] = createSignal(false);

    const [detail, { mutate, refetch }] = createResource(
        () => props.entryId ? { botId: props.botId, entryId: props.entryId } : false,
        async ({ botId, entryId }) => {
            const res = await api.payments.entry(botId, entryId);
            if (isOk(res.data)) return res.data.value as PaymentLedgerEntryDetailDto;
            toast?.error(firstError(res.data) ?? t('PAYMENTS_TOAST_LOAD_FAILED'));
            props.onClose();
            return undefined;
        },
    );

    usePaymentChanges(() => props.entryId ? props.botId : null, (kind, id) => {
        if (kind !== 'schedule' && (id === null || id === props.entryId)) void refetch();
    });

    const saveNote = async (note: string) => {
        const res = await api.payments.setNote(props.botId, props.entryId!, { note });
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('PAYMENTS_TOAST_NOTE_FAILED'));
            return;
        }
        mutate(res.data.value as PaymentLedgerEntryDetailDto);
        props.onChanged();
    };

    const resolve = async (paid: boolean) => {
        const entry = detail()?.entry;
        if (!entry) return;
        const confirmed = await showConfirm(
            t(paid ? 'PAYMENTS_RESOLVE_PAID_CONFIRM' : 'PAYMENTS_RESOLVE_NOT_PAID_CONFIRM', entry.playerName ?? '', formatCurrencyExact(entry.amount)),
            !paid,
            { confirmLabel: t(paid ? 'PAYMENTS_RESOLVE_PAID' : 'PAYMENTS_RESOLVE_NOT_PAID') },
        );
        if (!confirmed) return;

        setResolving(true);
        const res = await api.payments.resolve(props.botId, entry.id, { paid });
        setResolving(false);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('PAYMENTS_TOAST_RESOLVE_FAILED'));
            return;
        }
        mutate(res.data.value as PaymentLedgerEntryDetailDto);
        toast?.success(t(paid ? 'PAYMENTS_TOAST_MARKED_PAID' : 'PAYMENTS_TOAST_MARKED_NOT_PAID'));
        props.onChanged();
    };

    const title = () => {
        const entry = detail()?.entry;
        if (!entry) return t('PAYMENTS_ENTRY_TITLE');
        if (isBalanceChange(entry)) return t(balanceChangeLabelKey(entry));
        return t(entry.direction === 'Incoming' ? 'PAYMENTS_ENTRY_FROM' : 'PAYMENTS_ENTRY_TO', entry.playerName ?? '');
    };

    return (
        <Modal open={props.entryId !== null} onClose={props.onClose} title={<>{title()}</>} maxWidth="34rem" busy={resolving()}>
            <Show when={detail()} fallback={<div class={styles.modalLoading}><i class="fa-solid fa-spinner fa-spin fa-2x" /></div>}>
                {(loaded) => {
                    const entry = () => loaded().entry;
                    return (
                        <div class="v" style={{ gap: 'var(--spacing-lg)' }}>
                            <div class={styles.entryHero}>
                                <Show when={entry().playerName} fallback={<span class={`${styles.heroIcon} ${styles.directionNeutral}`}><i class={`fa-solid ${entryIcon(entry())}`} /></span>}>
                                    {(name) => <PlayerHeadAvatar name={name()} size={40} />}
                                </Show>
                                <div class="v" style={{ gap: 'var(--spacing-xs)', flex: '1', 'min-width': '0' }}>
                                    <span class={`${styles.heroAmount} ${signedAmount(entry()) < 0 ? styles.amountOut : styles.amountIn}`}>
                                        {signedAmount(entry()) < 0 ? '-' : '+'}{formatCurrencyExact(entry().amount)}
                                    </span>
                                    <span class={styles.muted}><Text val={`PAYMENTS_SOURCE_${entry().source.toUpperCase()}`} /> · {formatDateTime(entry().createdAt)}</span>
                                </div>
                                <Badge tone={PAYMENT_STATUS_TONE[entry().status] ?? 'neutral'} pill>
                                    <Text val={`PAYMENTS_STATUS_${entry().status.toUpperCase()}`} />
                                </Badge>
                            </div>

                            <Show when={entry().status === 'Unknown'}>
                                <div class={styles.reviewBox}>
                                    <span><i class="fa-solid fa-triangle-exclamation" /> <Text val="PAYMENTS_REVIEW_EXPLAINED" /></span>
                                    <div class="h" style={{ gap: 'var(--spacing-sm)', 'flex-wrap': 'wrap', 'justify-content': 'flex-end' }}>
                                        <button type="button" class="btn btn-secondary btn-sm" disabled={resolving()} onClick={() => void resolve(false)}>
                                            <i class="fa-solid fa-rotate-left" /> <Text val="PAYMENTS_RESOLVE_NOT_PAID" />
                                        </button>
                                        <button type="button" class="btn btn-primary btn-sm" disabled={resolving()} onClick={() => void resolve(true)}>
                                            <i class="fa-solid fa-check" /> <Text val="PAYMENTS_RESOLVE_PAID" />
                                        </button>
                                    </div>
                                </div>
                            </Show>

                            <dl class={styles.facts}>
                                <Show when={entry().balanceAfter != null}>
                                    <dt><Text val="PAYMENTS_FACT_BALANCE" /></dt>
                                    <dd>{formatCurrencyExact(entry().balanceBefore ?? 0)} <i class="fa-solid fa-arrow-right" /> {formatCurrencyExact(entry().balanceAfter!)}</dd>
                                </Show>
                                <Show when={entry().comparedToStaleValue}>
                                    <dt><Text val="PAYMENTS_FACT_MEASURED" /></dt>
                                    <dd><Text val="PAYMENTS_STALE_HINT" /></dd>
                                </Show>
                                <Show when={entry().reason}>
                                    {(reason) => <><dt><Text val="PAYMENTS_REASON" /></dt><dd>{reason()}</dd></>}
                                </Show>
                                <Show when={entry().declineReason}>
                                    {(reason) => <><dt><Text val="PAYMENTS_FACT_DECLINE_REASON" /></dt><dd>{reason()}</dd></>}
                                </Show>
                                <Show when={entry().messageText}>
                                    {(message) => (
                                        <>
                                            <dt><Text val="PAYMENTS_FACT_MESSAGE" /></dt>
                                            <dd>
                                                {message()}{' '}
                                                <Badge tone={entry().messageStatus === 'Sent' ? 'success' : entry().messageStatus === 'Failed' ? 'error' : 'neutral'}>
                                                    <Text val={`PAYMENTS_MESSAGE_${entry().messageStatus.toUpperCase()}`} />
                                                </Badge>
                                            </dd>
                                        </>
                                    )}
                                </Show>
                                <Show when={entry().resolvedBy}>
                                    {(resolvedBy) => <><dt><Text val="PAYMENTS_FACT_RESOLVED_BY" /></dt><dd>{resolvedBy()} · {formatDateTime(entry().resolvedAt)}</dd></>}
                                </Show>
                                <Show when={referenceHref(entry())}>
                                    {(href) => (
                                        <>
                                            <dt><Text val="PAYMENTS_FACT_BELONGS_TO" /></dt>
                                            <dd><A href={href()} onClick={props.onClose}><Text val={`PAYMENTS_REFERENCE_${entry().source.toUpperCase()}`} /> <i class="fa-solid fa-arrow-up-right-from-square" /></A></dd>
                                        </>
                                    )}
                                </Show>
                            </dl>

                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="PAYMENTS_NOTE" /></span>
                                <InlineEdit value={entry().note ?? ''} placeholder={t('PAYMENTS_NOTE_PLACEHOLDER')} onSave={saveNote} multiline ariaLabel={t('PAYMENTS_NOTE')} />
                            </label>

                            <div class="v" style={{ gap: 'var(--spacing-sm)' }}>
                                <span class={styles.fieldLabel}><Text val="PAYMENTS_JOURNAL" /></span>
                                <ol class={styles.timeline}>
                                    <For each={loaded().events}>
                                        {(journalEvent) => (
                                            <li>
                                                <span class={styles.timelineTime}>{formatDateTime(journalEvent.createdAt)}</span>
                                                <span><Text val={`PAYMENTS_EVENT_${journalEvent.kind.toUpperCase()}`} /></span>
                                                <Show when={journalEvent.detail}>
                                                    {(eventDetail) => journalEvent.kind.startsWith('message_')
                                                        ? <span class={styles.muted}><Text val={`PAYMENTS_MESSAGE_OUTCOME_${eventDetail().toUpperCase()}`} /></span>
                                                        : <code class={styles.timelineDetail}>{stripColorCodes(eventDetail())}</code>}
                                                </Show>
                                            </li>
                                        )}
                                    </For>
                                </ol>
                            </div>
                        </div>
                    );
                }}
            </Show>
        </Modal>
    );
}
